"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useSaved } from "@/components/context/saved-context";
import { saveItem, unsaveItem } from "@/app/actions/saved";

interface UseSavedStateProps {
  itemId: string;
  itemType: "post" | "listing";
  initialSaved: boolean;
  currentUserId?: string;
}

export function useSavedState({
  itemId,
  itemType,
  initialSaved,
  currentUserId,
}: UseSavedStateProps) {
  const { notifySavedChange, subscribeSavedChange, getSavedState, setSavedState } = useSaved();

  const [saved, setSaved] = useState(() => {
    const cached = getSavedState(itemId, itemType);
    return cached ? cached.saved : initialSaved;
  });
  const [loading, setLoading] = useState(false);
  const savedRef = useRef(saved);
  savedRef.current = saved;

  useEffect(() => {
    if (!getSavedState(itemId, itemType)) {
      setSavedState(itemId, itemType, initialSaved);
    }
  }, [itemId, itemType, initialSaved, getSavedState, setSavedState]);

  useEffect(() => {
    return subscribeSavedChange((changedItemId, changedItemType, changedSaved) => {
      if (changedItemId === itemId && changedItemType === itemType) {
        setSaved(changedSaved);
      }
    });
  }, [itemId, itemType, subscribeSavedChange]);

  const toggleSaved = useCallback(async () => {
    if (!currentUserId || loading) return;

    setLoading(true);
    const previousSaved = savedRef.current;

    try {
      const newSaved = !previousSaved;
      setSaved(newSaved);
      notifySavedChange(itemId, itemType, newSaved);

      if (newSaved) {
        await saveItem({ userId: currentUserId, itemType, itemId });
      } else {
        await unsaveItem({ userId: currentUserId, itemType, itemId });
      }
    } catch (error) {
      console.error("Error toggling saved:", error);
      setSaved(previousSaved);
      notifySavedChange(itemId, itemType, previousSaved);
    } finally {
      setLoading(false);
    }
  }, [itemId, itemType, currentUserId, loading, notifySavedChange]);

  return {
    saved,
    loading,
    toggleSaved,
  };
}
